import "@/App.css";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";

// Scrolls to the top of the page on every route change.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

import HomePage from "./pages/public/HomePage";
import ImportantLinksPage from "./pages/public/ImportantLinksPage";
import ServiceDetailPage from "./pages/public/ServiceDetailPage";
import ContactPage from "./pages/public/ContactPage";
import UpdatesPage from "./pages/public/UpdatesPage";
import LoginPage from "./pages/auth/LoginPage";
import PortalLayout from "./pages/portal/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import AgentLocations from "./pages/portal/AgentLocations";
import DocumentHub from "./pages/portal/DocumentHub";
import HolidayManagement from "./pages/portal/HolidayManagement";
import NewsManagement from "./pages/portal/NewsManagement";
import UserManagement from "./pages/portal/UserManagement";
import EmployeesDirectory from "./pages/portal/EmployeesDirectory";
import ImportantLinksManagement from "./pages/portal/ImportantLinksManagement";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/get-quote" element={<Navigate to="/#quote" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/important-links" element={<ImportantLinksPage />} />
          <Route path="/directors" element={<Navigate to="/" replace />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/employee" element={<Navigate to="/login" replace />} />

          <Route path="/portal" element={<Protected><PortalLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="agents" element={<AgentLocations />} />
            <Route path="employees" element={<EmployeesDirectory />} />
            <Route path="documents" element={<DocumentHub />} />
            <Route path="holidays" element={<HolidayManagement />} />
            <Route path="news" element={<NewsManagement />} />
            <Route path="important-links" element={<ImportantLinksManagement />} />
            <Route path="users" element={<Protected adminOnly><UserManagement /></Protected>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
